import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card } from '@/components/common';
import { usePOSPsLookup } from '@/hooks/useBrokers';
import { usePospStatement } from '@/hooks/useCommissions';
import { commissionService } from '@/services/commissions';
import { formatCurrency, formatDate } from '@/utils';
import { currentFinancialYear, rowClasses, statusBadge, statusDetail } from './pospLedgerStatement';

/**
 * #692 -- Commissions > POSP ledger. One POSP, one financial year (1 Apr - 31 Mar): "Balance b/f", every
 * entry with Dr / Cr and the running balance as Cr (we owe the agent) or Dr, then "Balance c/f". Policy lines
 * carry Due / Part paid / Paid; Paid lines are grey. `own` is the agent's own view (no picker; the server
 * answers only for the logged-in agent).
 */
const SELECT = 'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function fyOptions(years: string[] | undefined, selected: string): string[] {
  const set = new Set([...(years ?? []), selected]);
  return Array.from(set).sort().reverse();
}

export function PospLedgerStatementPage({ own = false }: { own?: boolean }) {
  const [pospId, setPospId] = useState('');
  const [fy, setFy] = useState(currentFinancialYear());
  const [busy, setBusy] = useState<'csv' | 'pdf' | null>(null);
  const posps = usePOSPsLookup(undefined);
  const enabledId = own ? undefined : pospId || undefined;
  const statement = usePospStatement(enabledId, fy, own || Boolean(pospId));
  const data = statement.data;

  const download = async (kind: 'csv' | 'pdf') => {
    setBusy(kind);
    try {
      const blob = await commissionService.downloadPospStatement({ posp_id: enabledId, financial_year: fy }, kind);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `posp-ledger-${fy}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not export the statement just now.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="POSP ledger" subtitle="Every commission earned, paid, deducted and clawed back, by financial year" />
      <Card padding="md">
        <div className="flex flex-wrap items-end gap-3">
          {!own && (
            <label className="text-sm text-gray-700 dark:text-gray-300">
              <span className="mb-1 block">POSP</span>
              <select aria-label="POSP" className={SELECT} value={pospId} onChange={(e) => setPospId(e.target.value)}>
                <option value="">Choose a POSP</option>
                {(posps.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{(p as { full_name?: string }).full_name || p.posp_code} ({p.posp_code})</option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm text-gray-700 dark:text-gray-300">
            <span className="mb-1 block">Financial year</span>
            <select aria-label="Financial year" className={SELECT} value={fy} onChange={(e) => setFy(e.target.value)}>
              {fyOptions(data?.financial_years, fy).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" isLoading={busy === 'csv'} disabled={!data} onClick={() => download('csv')}>Export CSV</Button>
            <Button variant="outline" size="sm" isLoading={busy === 'pdf'} disabled={!data} onClick={() => download('pdf')}>Export PDF</Button>
          </div>
        </div>
      </Card>

      {!own && !pospId && <p className="text-sm text-gray-500 dark:text-gray-400">Choose a POSP to see the ledger.</p>}
      {statement.isError && <p role="alert" className="text-sm text-amber-700 dark:text-amber-400">Could not load this ledger. That is not the same as there being no entries - please try again.</p>}
      {data && (
        <Card padding="none">
          <div className="flex flex-wrap gap-6 border-b border-gray-100 px-6 py-4 text-sm dark:border-gray-800">
            <div><p className="text-xs text-gray-500 dark:text-gray-400">Due total</p><p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(data.due_total)}</p></div>
            <div><p className="text-xs text-gray-500 dark:text-gray-400">{data.balance_cf.label}</p><p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{data.balance_cf.text}</p></div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {data.date_from} to {data.date_to}
              {data.year_close?.status === 'locked' && <Badge variant="info" size="sm">Year closed</Badge>}
              {!data.trial_balance.balanced && <span className="ml-2 text-red-700 dark:text-red-400">Trial balance does not balance</span>}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {['Date', 'Entry', 'Policy', 'Customer', 'Insurer', 'Premium', 'Dr', 'Cr', 'Balance', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                <tr data-testid="balance-bf" className="font-medium text-gray-900 dark:text-gray-100">
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(data.balance_bf.date)}</td>
                  <td className="px-4 py-3" colSpan={7}>{data.balance_bf.label}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{data.balance_bf.text}</td>
                  <td />
                </tr>
                {data.results.map((r) => {
                  const badge = statusBadge(r);
                  const detail = statusDetail(r);
                  return (
                    <tr key={r.id} data-status={r.status ?? ''} className={rowClasses(r)}>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.date)}</td>
                      <td className="px-4 py-3">{r.entry_label}{r.bank_confirmed && <span className="ml-1 text-xs">(bank confirmed)</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.policy_id ? <Link className="underline" to={`/policies/${r.policy_id}`}>{r.policy_number}</Link> : r.policy_number ?? ''}</td>
                      <td className="px-4 py-3">{r.customer ?? ''}</td>
                      <td className="px-4 py-3">{r.insurer ?? ''}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.premium ? formatCurrency(r.premium) : ''}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.debit ? formatCurrency(r.debit) : ''}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.credit ? formatCurrency(r.credit) : ''}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{r.running_text}</td>
                      <td className="px-4 py-3">
                        {badge && <Badge variant={badge.variant} size="sm">{badge.label}</Badge>}
                        {detail && <span className="block text-xs">{detail}</span>}
                      </td>
                    </tr>
                  );
                })}
                <tr data-testid="balance-cf" className="font-semibold text-gray-900 dark:text-gray-100">
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(data.balance_cf.date)}</td>
                  <td className="px-4 py-3" colSpan={5}>{data.balance_cf.label}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatCurrency(data.totals.debit)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatCurrency(data.totals.credit)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{data.balance_cf.text}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default PospLedgerStatementPage;
